import { Link, useToast } from '@chakra-ui/react';
import moment from 'moment';
import { useState } from 'react';

import { AsyncJobStatusEnum } from '../../types/asyncExport.types';
import { TooltipStandard } from '../tooltip/TooltipStandard';
import { cellsMessages } from './variables/cellsMessages';

interface ExportDownloadLinkCellProps {
	row: {
		id: number;
		async_job?: {
			status: AsyncJobStatusEnum;
			download_url: string | null;
			finish_date: string | null;
		};
	};
}

const EXPIRATION_DAYS = 7;

const calculateDaysUntilExpiry = (finishDate: string | null): number | null => {
	if (!finishDate) return null;

	const finish = moment.utc(finishDate);
	const expiry = finish.clone().add(EXPIRATION_DAYS, 'days');
	const now = moment();
	const daysLeft = expiry.diff(now, 'days');

	return daysLeft > 0 ? daysLeft : 0;
};

const extractFileNameFromUrl = (url: string): string | null => {
	try {
		const urlObj = new URL(url);
		const contentDisposition = urlObj.searchParams.get(
			'response-content-disposition'
		);

		if (contentDisposition) {
			const decoded = decodeURIComponent(contentDisposition);
			const fileNameMatch = decoded.match(/filename="(.+)"/);
			if (fileNameMatch?.[1]) {
				return fileNameMatch[1];
			}
			const fileNameMatchNoQuotes = decoded.match(/filename=(.+)/);
			if (fileNameMatchNoQuotes?.[1]) {
				return fileNameMatchNoQuotes[1].trim();
			}
		}

		const pathParts = urlObj.pathname.split('/');
		const lastPart = pathParts[pathParts.length - 1];
		if (lastPart && lastPart.includes('.')) {
			return lastPart.split('?')[0];
		}

		return null;
	} catch {
		return null;
	}
};

export const ExportDownloadLinkCell = ({
	row,
}: ExportDownloadLinkCellProps) => {
	const toast = useToast();
	const [isDownloading, setIsDownloading] = useState(false);
	const asyncJob = row.async_job;

	if (!asyncJob?.download_url) {
		return <span>{cellsMessages.LABELS.NO_DATA}</span>;
	}

	const { status, finish_date } = asyncJob;

	if (status === AsyncJobStatusEnum.EXPIRED) {
		return (
			<TooltipStandard label={cellsMessages.TOOLTIPS.FILE_EXPIRED}>
				<span
					style={{
						color: 'gray',
						cursor: 'not-allowed',
					}}
				>
					{cellsMessages.LABELS.FILE_EXPIRED}
				</span>
			</TooltipStandard>
		);
	}

	if (status === AsyncJobStatusEnum.COMPLETED) {
		const daysLeft = calculateDaysUntilExpiry(finish_date);

		const handleDownload = (e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();

			if (isDownloading) {
				return;
			}

			const downloadUrl = asyncJob.download_url;

			if (!downloadUrl) {
				toast({
					title: cellsMessages.MSG_FILE_NOT_AVAILABLE.title,
					description:
						cellsMessages.MSG_FILE_NOT_AVAILABLE.description,
					status: 'warning',
					duration: 8000,
					isClosable: true,
					position: 'bottom-left',
				});
				return;
			}

			setIsDownloading(true);

			toast({
				title: cellsMessages.MSG_DOWNLOAD_STARTING.title,
				description: cellsMessages.MSG_DOWNLOAD_STARTING.description,
				status: cellsMessages.MSG_DOWNLOAD_STARTING.status,
				duration: 5000,
				isClosable: true,
				position: 'bottom-left',
			});

			try {
				const fileName = extractFileNameFromUrl(downloadUrl);

				const link = document.createElement('a');
				link.href = downloadUrl;
				if (fileName) {
					link.setAttribute('download', fileName);
				}
				document.body.appendChild(link);
				link.click();
				link.remove();
			} catch (error: unknown) {
				toast({
					title: cellsMessages.MSG_ERROR_DOWNLOAD.title,
					description: cellsMessages.MSG_ERROR_DOWNLOAD.description,
					status: 'error',
					duration: 8000,
					isClosable: true,
					position: 'bottom-left',
				});
			}
		};

		const tooltipText = isDownloading
			? cellsMessages.TOOLTIPS.DOWNLOAD_DISABLED
			: daysLeft !== null && daysLeft > 0
			? cellsMessages.TOOLTIPS.FILE_EXPIRES_DAYS(daysLeft)
			: cellsMessages.TOOLTIPS.FILE_EXPIRED_LINK;

		return (
			<TooltipStandard label={tooltipText}>
				<span
					style={{
						display: 'inline-block',
						cursor: isDownloading ? 'not-allowed' : 'pointer',
					}}
				>
					<Link
						onClick={handleDownload}
						color={isDownloading ? 'gray.400' : 'blue.500'}
						textDecoration='underline'
						cursor={isDownloading ? 'not-allowed' : 'pointer'}
						_hover={isDownloading ? {} : { color: 'blue.600' }}
						pointerEvents={isDownloading ? 'none' : 'auto'}
					>
						{cellsMessages.LABELS.DOWNLOAD_FILE}
					</Link>
				</span>
			</TooltipStandard>
		);
	}

	return <span>{cellsMessages.LABELS.NO_DATA}</span>;
};
